/**
 * Add a product — the minimal form, ruled into c34 on 2026-08-07.
 *
 * At c34 this was *"explicitly not a first draft of `screens.md` §1's check-digit screen"* —
 * the shortest honest path from an empty catalogue to a product that exists. **c92 is where
 * §1's teaching arrives**, on this field rather than on a new screen, because the ruling
 * that deferred it deferred a *behaviour* and this is the form that behaviour belongs to:
 * the offending digit marked in place, the rule and the likely cause in that order, and a
 * one-click fix carrying the corrected number.
 *
 * What did **not** arrive with it is §1's onboarding context — the three-step signup, the
 * registry match, the "I don't know my prefix" door. Those are screens, they are Phase 3's
 * onboarding, and none of them is a property of this field.
 *
 * The rules live in `create-product.ts` as pure functions — see its header for why the
 * correction is offered and never applied, and for the R12 asymmetry that lets a button here
 * carry a number a REST client only ever sees as prose.
 *
 * ## The workspace is chosen from a list, and Phase 2's list has one entry
 *
 * `products.workspace_id` is `NOT NULL`, and until c34 nothing exposed a workspace id at all
 * — which is what blocked this form. `orgs.current` now returns the **list**: one entry
 * renders no chooser and is used silently; several would need one, and R9 defers workspace
 * management out of Phase 2, so the form says so rather than picking. Zero entries is a real
 * state too — an organization that predates the bootstrap writing one — and it is named
 * rather than crashed into a required-field error nobody can satisfy.
 */
import { useState, type ReactNode } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate } from "@tanstack/react-router";

import { Button, Input, Surface } from "@packagepage/ui";

import { problemOf } from "../api/client.js";
import { api } from "../api/procedures.js";
import {
  EMPTY_FORM,
  applyGtinCorrection,
  canSubmit,
  checkDigitTeaching,
  parseCreateForm,
  validateCreateForm,
  type CreateProductForm,
} from "./create-product.js";

import "@packagepage/ui/input.css";
import "@packagepage/ui/button.css";
import "./create-product.css";

export function CreateProduct(): ReactNode {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const org = useQuery({
    queryKey: ["orgs", "current"],
    queryFn: () => api.currentOrg(),
  });

  const [form, setForm] = useState<CreateProductForm>(EMPTY_FORM);
  // Errors render only after a submit attempt. A form that reddens on the first keystroke
  // is a form that is wrong about the user's intent — they have not finished typing.
  const [attempted, setAttempted] = useState(false);

  const workspaces = org.data?.workspaces ?? [];
  // The org's own name is a sensible default and not a rule: `products.brand_name` is
  // per-product because a brand can ship under a name that is not the account's.
  const brandDefault = org.data?.name ?? "";
  const effective: CreateProductForm = {
    ...form,
    brandName: form.brandName === "" ? brandDefault : form.brandName,
    workspaceId: form.workspaceId === "" ? (workspaces[0]?.id ?? "") : form.workspaceId,
  };

  const errors = attempted ? validateCreateForm(effective) : {};
  /**
   * The check-digit teaching, and it renders on the **same** trigger as every other error:
   * after a submit attempt, never on the first keystroke. A form that reddens while somebody
   * is still typing is a form that is wrong about their intent — and a fourteen-digit field
   * is wrong about it thirteen times.
   */
  const teaching = attempted ? checkDigitTeaching(effective) : null;

  const create = useMutation({
    mutationFn: () => api.createProduct(parseCreateForm(effective)),
    onSuccess: async (created) => {
      void queryClient.invalidateQueries({ queryKey: ["products"] });
      // Straight to the product, which is where the next thing to do is — publishing.
      // Landing back on the list would make the user find what they just made.
      await navigate({ to: "/products/$productId", params: { productId: created.id } });
    },
  });

  if (org.isPending) {
    return (
      <Surface>
        <p data-screen-label="Add a product">Loading&hellip;</p>
      </Surface>
    );
  }

  if (workspaces.length === 0) {
    return (
      <Surface tone="warn" emphasis="filled">
        <div data-screen-label="Add a product">
          <strong>This organisation has no workspace to file a product in</strong>
          <p>
            Every product belongs to a workspace, and one is created with the
            organisation. This account has none, which means it was created before that
            was true — support can add one. Nothing about your existing codes is affected.
          </p>
          <p>
            <Link to="/products">Back to the catalogue</Link>
          </p>
        </div>
      </Surface>
    );
  }

  const problem = create.error === null ? null : problemOf(create.error);

  return (
    <div data-screen-label="Add a product" className="pp-create">
      <Surface>
        <form
          className="pp-create__form"
          onSubmit={(e) => {
            e.preventDefault();
            setAttempted(true);
            if (canSubmit(effective)) create.mutate();
          }}
        >
          <div className="pp-create__title">Add a product</div>
          <p className="pp-create__lede">
            A product is created as a <strong>draft</strong> — its code answers nothing
            until you publish it, so there is nothing to undo if you change your mind.
          </p>

          {/*
            Two failures, two treatments, and the difference is whether an answer exists.

            `INVALID_CHECK_DIGIT` has exactly one correct value and the schema computes it,
            so the field gets `screens.md` §1's teaching: the rule, the likely cause, the
            offending digit marked in place, and a fix button carrying the number.
            `INVALID_GTIN` has no computable answer — R12's whole reason for two codes — so
            it gets the rule and the cause and **no button**, because a fix that guessed
            would be inventing somebody's product identifier.

            `onFix` is what makes the correction *offered* rather than applied. The field
            holds what the person typed until they press it; §9 rules the platform never
            rewrites a customer's GTIN, and a form that arrived pre-corrected would have
            made that decision for them.
          */}
          <Input
            label="Code (GTIN-14)"
            value={form.gtin14}
            mono
            inputMode="numeric"
            autoComplete="off"
            placeholder="14 digits"
            onChange={(e) => setForm({ ...form, gtin14: e.target.value })}
            onFix={(value) => setForm(applyGtinCorrection(form, value))}
            {...(teaching !== null
              ? { validation: teaching }
              : errors.gtin14 === undefined
                ? {}
                : {
                    validation: {
                      state: "invalid" as const,
                      rule: errors.gtin14,
                      // The cause, not a restatement of the rule. The overwhelmingly common
                      // reason a code arrives with the wrong number of digits is a
                      // spreadsheet that dropped a leading zero or rewrote the number in
                      // scientific notation — the same cause the list's zero-results state
                      // names, because it is the same spreadsheet.
                      cause:
                        "A code copied from a spreadsheet often loses a leading zero, or " +
                        "arrives rewritten as 8.71E+12 — check the cell is formatted as Text.",
                    },
                  })}
          />

          <Input
            label="Brand name"
            value={effective.brandName}
            onChange={(e) => setForm({ ...form, brandName: e.target.value })}
            {...(errors.brandName === undefined
              ? {}
              : {
                  validation: {
                    state: "invalid" as const,
                    rule: errors.brandName,
                    cause:
                      "This is the name shoppers see on the product page, and it defaults " +
                      "to your organisation's name.",
                  },
                })}
          />

          <Input
            label="SKU (optional)"
            value={form.sku}
            mono
            autoComplete="off"
            onChange={(e) => setForm({ ...form, sku: e.target.value })}
          />

          <Input
            label="Product name (optional)"
            value={form.nameEn}
            onChange={(e) => setForm({ ...form, nameEn: e.target.value })}
          />
          <p className="pp-create__note">
            English, for now. Names are stored per language and the product page switches
            at the field — adding other languages happens on the product itself.
          </p>

          {/*
            One workspace renders no chooser, exactly as one membership renders no org
            picker. Several would need one and Phase 2 has none to give, so it says which
            it used rather than choosing invisibly.
          */}
          {workspaces.length > 1 ? (
            <p className="pp-create__note">
              This organisation has {workspaces.length} workspaces. Choosing between them
              is not built yet, so this product goes into{" "}
              <strong>{workspaces[0]?.name}</strong>.
            </p>
          ) : null}

          {problem === null ? null : (
            <Surface tone="error" emphasis="filled">
              <div>
                <strong>{problem.title}</strong>
                <p>{problem.detail}</p>
              </div>
            </Surface>
          )}

          <div className="pp-create__actions">
            <Button type="submit" variant="primary" loading={create.isPending}>
              {create.isPending ? "Adding…" : "Add product"}
            </Button>
            <Link to="/products" className="pp-create__cancel">
              Cancel
            </Link>
          </div>
        </form>
      </Surface>
    </div>
  );
}
